import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRefetchOn } from "@/hooks/useRefetchOn";
import { Can } from "@/components/auth/Can";
import { OPTION_CODES } from "@/constants/optionCodes";
import { resolveAiBlockedMessage } from "@/constants/clientAccess";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { BackButton } from "@/common/BackButton";
import { Box, Typography, IconButton, Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import {
  CustomSectionTitle,
  CustomTextInput,
  CustomSingleSelect,
  CustomCategorySelectSingle,
  CustomFileUploader,
  CustomCategorySelect,
  FixedActionBar,
  CustomCheckbox,
  CustomFormNavigationGuard,
  CustomButton,
  CustomLoading,
  CustomModal,
  type CategoryOption,
  CustomPdfPreviewCanvas,
  useScrollToFirstError,
  usePreviewDrawerUnsaved,
} from "@ippon/shared-components";
import InitialLoadError from "../../../components/ErrorState/InitialLoadError";
import {
  getApiErrorType,
  type ApiErrorType,
} from "../../../utils/apiErrorType";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";
import {
  AREA_TYPE_OPTIONS,
  AREA_TYPE_LABELS,
  type AreaType,
  type Unit as UnitRecord,
} from "@ippon/shared-types";

// レガシー BE データが area_type に日本語表示名 (例: "専有部") を返してくる
// 場合があるため、コード値 (private/common/...) に正規化する。
// 既にコード値ならそのまま返す。それ以外は "" (未選択) として扱う。
const LABEL_TO_AREA_TYPE: Record<string, AreaType> = Object.fromEntries(
  (Object.entries(AREA_TYPE_LABELS) as [AreaType, string][]).map(
    ([code, label]) => [label, code],
  ),
);
const normalizeAreaType = (raw: unknown): AreaType | "" => {
  if (typeof raw !== "string" || raw === "") return "";
  if (raw in AREA_TYPE_LABELS) return raw as AreaType;
  if (raw in LABEL_TO_AREA_TYPE) return LABEL_TO_AREA_TYPE[raw];
  return "";
};
import { fetchUnitsByProperty } from "../../../store/unitSlice";
import type { RepairRow } from "../../../components/RepairsTable";
import { statusOptions } from "../../../components/RepairsTable";
// import LinkedExpensesSection from "../../../components/PropertyDetail/Repair/LinkedExpensesSection";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  updateRepair,
  uploadRepairEstimate,
  deleteRepairEstimate,
  analyzeExistingRepairEstimates,
  selectRepairEstimate,
  fetchRepairById,
} from "../../../store/repairSlice";
import {
  fetchCategoryManagement,
  upsertCategoryManagement,
} from "../../../store/categoryManagementSlice";
import type { RootState } from "../../../store";
import ipponApi from "../../../config/axios";
import { useGlobalToast } from "../../../hooks/useGlobalToast";

const initialCategoryOptions: CategoryOption[] = [
  { label: "衛生器具", value: "衛生器具" },
  { label: "外装材", value: "外装材" },
  { label: "躯体等", value: "躯体等" },
  { label: "建具類", value: "建具類" },
  { label: "外部金物等", value: "外部金物等" },
  { label: "自動ドア・シャッター等", value: "自動ドア・シャッター等" },
  { label: "内装", value: "内装" },
  { label: "外構（舗装等）", value: "外構（舗装等）" },
  { label: "変電設備", value: "変電設備" },
  { label: "動力盤、電灯盤、ケーブル等", value: "動力盤、電灯盤、ケーブル等" },
  { label: "電灯コンセント及び配線", value: "電灯コンセント及び配線" },
  { label: "照明設備", value: "照明設備" },
  { label: "電話・TV・放送設備等", value: "電話・TV・放送設備等" },
  { label: "発電機・蓄電池等", value: "発電機・蓄電池等" },
  { label: "空調換気設備", value: "空調換気設備" },
  { label: "受水槽・高架水槽・ポンプ", value: "受水槽・高架水槽・ポンプ" },
  { label: "給水設備", value: "給水設備" },
  { label: "給湯設備", value: "給湯設備" },
  { label: "排水設備", value: "排水設備" },
  { label: "配管設備", value: "配管設備" },
  { label: "昇降機設備（ELV）", value: "昇降機設備（ELV）" },
  { label: "機械式駐車場", value: "機械式駐車場" },
  { label: "消火設備", value: "消火設備" },
  { label: "警報設備", value: "警報設備" },
  { label: "避雷設備", value: "避雷設備" },
  { label: "その他", value: "その他" },
  { label: "屋上防水・屋根", value: "屋上防水・屋根" },
  { label: "防火設備", value: "防火設備" },
  { label: "消防設備点検（複合）", value: "消防設備点検（複合）" },
  { label: "特定建築物定期検査（複合）", value: "特定建築物定期検査（複合）" },
  { label: "防火設備点検（複合）", value: "防火設備点検（複合）" },
  { label: "建築設備定期検査（複合）", value: "建築設備定期検査（複合）" },
  { label: "廃棄物処理", value: "廃棄物処理" },
  {
    label: "非常照明（建築設備、特建、消防）",
    value: "非常照明（建築設備、特建、消防）",
  },
  { label: "誘導灯", value: "誘導灯" },
  { label: "法定点検実施費用", value: "法定点検実施費用" },
  { label: "排煙設備", value: "排煙設備" },
  { label: "リニューアル工事", value: "リニューアル工事" },
  { label: "原状回復工事", value: "原状回復工事" },
];

type RepairEstimate = {
  repair_estimate_id: string | number;
  repair_image?: string;
  ai_assessed?: boolean;
  ai_error?: string;
  status?: string | boolean;
  is_selected?: boolean;
  linked_expense_id?: string;
  estimate_company_name?: string;
  estimate_amount?: number | string;
  ai_overall_decision?: string;
  detailed_evaluation?: Record<string, unknown>;
  anomaly_checks?: Record<string, unknown>;
  file_name?: string;
};

/** GET のたびに署名クエリだけ変わる URL を同一画像とみなす（ポーリングで全サムネが再取得されないように） */
function signedUrlObjectKey(url: string | undefined): string {
  if (!url) return "";
  try {
    return new URL(url).pathname;
  } catch {
    return url.split("?")[0] || url;
  }
}

function reusePreviousSignedUrlIfSameObject(
  prevUrl: string | undefined,
  nextUrl: string | undefined,
): string | undefined {
  if (!prevUrl || !nextUrl) return nextUrl;
  if (signedUrlObjectKey(prevUrl) === signedUrlObjectKey(nextUrl)) {
    return prevUrl;
  }
  return nextUrl;
}

function applyPreservedRepairImage(
  prevEst: RepairEstimate | undefined,
  est: RepairEstimate,
): RepairEstimate {
  const img = reusePreviousSignedUrlIfSameObject(
    prevEst?.repair_image,
    est.repair_image,
  );
  if (img === est.repair_image) return est;
  return { ...est, repair_image: img };
}

function mergeRepairImagesPreserveSignedUrls(
  prevImages: unknown,
  nextImages: unknown,
): unknown {
  if (!Array.isArray(nextImages)) return nextImages;
  const prevList = Array.isArray(prevImages) ? prevImages : [];
  const prevByKey = new Map<string, string>();
  for (const u of prevList) {
    if (typeof u !== "string") continue;
    prevByKey.set(signedUrlObjectKey(u), u);
  }
  return nextImages.map((newUrl: unknown) => {
    if (typeof newUrl !== "string") return newUrl;
    const prevUrl = prevByKey.get(signedUrlObjectKey(newUrl));
    return reusePreviousSignedUrlIfSameObject(prevUrl, newUrl) ?? newUrl;
  });
}

const getEstimateDisplayTitle = (
  est: { file_name?: string; estimate_company_name?: string } | undefined,
): string => {
  if (!est) return "見積書";
  const cleaned =
    (est.file_name || "")
      .replace(/^[a-f0-9-]+_/, "")
      .replace(/^【御見積】/, "")
      .replace(/\.pdf$/i, "")
      .replace(/ \(\d+\)$/, "") || "";
  return cleaned || est.estimate_company_name || "見積書";
};

type RepairDetailData =
  | (RepairRow & {
      property_id?: string;
      repair_id?: string;
      repair_title?: string;
    })
  | null;

type UpdateRepairPayload = {
  repair_id: string;
  repair_title?: string;
  area_type?: string;
  area_name?: string;
  target_unit_id?: string;
  target_name_snapshot?: string;
  status?: string;
  category?: string | string[];
  expenditure_type?: "capex" | "opex";
  repair_images?: any[];
  estimate_images?: any[];
  remove_repair_images?: string[];
};

const RE_ESTIMATE_SESSION_KEY = "ippon_re_estimate_pending_v1";
const RE_ESTIMATE_TTL_MS = 20 * 60 * 1000;
/** 再見積もり開始直後、GET が古い ai_error 付き行を返す間はセッションを消さない */
const RE_ESTIMATE_STALE_ERROR_GRACE_MS = 45_000;

/** 見積書単位で analyze キック済みタイムスタンプを管理（一覧→詳細の再キック防止） */
const ESTIMATE_KICK_SESSION_KEY = "ippon_estimate_analyze_kick_v2";
const ESTIMATE_KICK_TTL_MS = 25 * 60 * 1000;

function readEstimateKickStore(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(ESTIMATE_KICK_SESSION_KEY);
    if (!raw) return {};
    const p = JSON.parse(raw) as unknown;
    if (typeof p !== "object" || p === null || Array.isArray(p)) return {};
    return p as Record<string, number>;
  } catch {
    return {};
  }
}

function markEstimateKicked(estimateIds: string[]) {
  try {
    const store = readEstimateKickStore();
    const now = Date.now();
    for (const id of estimateIds) store[String(id)] = now;
    const cutoff = now - ESTIMATE_KICK_TTL_MS;
    for (const key of Object.keys(store)) {
      if (store[key] < cutoff) delete store[key];
    }
    sessionStorage.setItem(ESTIMATE_KICK_SESSION_KEY, JSON.stringify(store));
  } catch {
    /* ignore */
  }
}

type ReEstimatePendingStore = Record<string, Record<string, number>>;

function readReEstimateStore(): ReEstimatePendingStore {
  try {
    const raw = sessionStorage.getItem(RE_ESTIMATE_SESSION_KEY);
    if (!raw) return {};
    const p = JSON.parse(raw) as unknown;
    return typeof p === "object" && p !== null && !Array.isArray(p)
      ? (p as ReEstimatePendingStore)
      : {};
  } catch {
    return {};
  }
}

function writeReEstimateStore(store: ReEstimatePendingStore) {
  try {
    sessionStorage.setItem(RE_ESTIMATE_SESSION_KEY, JSON.stringify(store));
  } catch {
    /* ストレージ不可時は無視 */
  }
}

function markReEstimatePending(repairId: string, estimateId: string) {
  const store = readReEstimateStore();
  const rid = String(repairId);
  const eid = String(estimateId);
  if (!store[rid]) store[rid] = {};
  store[rid][eid] = Date.now();
  writeReEstimateStore(store);
}

function clearReEstimatePending(repairId: string, estimateId: string) {
  const store = readReEstimateStore();
  const rid = String(repairId);
  const eid = String(estimateId);
  if (store[rid]) {
    delete store[rid][eid];
    if (Object.keys(store[rid]).length === 0) delete store[rid];
  }
  writeReEstimateStore(store);
}

function getActiveReEstimatePendingIds(repairId: string): Set<string> {
  const store = readReEstimateStore();
  const rid = String(repairId);
  const forRepair = store[rid];
  if (!forRepair) return new Set();
  const now = Date.now();
  const pruned: Record<string, number> = {};
  const active = new Set<string>();
  for (const [eid, started] of Object.entries(forRepair)) {
    if (now - started < RE_ESTIMATE_TTL_MS) {
      pruned[eid] = started;
      active.add(eid);
    }
  }
  if (Object.keys(pruned).length === 0) {
    delete store[rid];
  } else {
    store[rid] = pruned;
  }
  writeReEstimateStore(store);
  return active;
}

interface RepairDetailPageProps {
  /** ドロワー等で埋め込む際に使用: URL パラメータより優先される repair_id */
  overrideRepairId?: string;
  /** 埋め込みモード (現状は id 上書きのみ。将来的にヘッダ簡略化等に使う想定) */
  embedded?: boolean;
}

const RepairDetailPage: React.FC<RepairDetailPageProps> = ({
  overrideRepairId,
  embedded,
}) => {
  const params = useParams<{ id: string }>();
  const { state } = useLocation() as {
    state?: {
      repair?: RepairRow;
      showToast?: { message: string; type: string };
    };
  };
  const navigate = useNavigate();
  const [currentData, setCurrentData] = useState<RepairDetailData>(
    state?.repair || null,
  );
  const currentDataRef = useRef<RepairDetailData>(state?.repair || null);
  const [propertyName, setPropertyName] = useState<string>(
    (state as any)?.propName || "",
  );
  const pollingRef = useRef<any>(null);
  const fetchOnceRef = useRef<(() => Promise<void>) | null>(null);
  // unmount 後に setInterval が走らないよう保護するフラグ
  const unmountedRef = useRef<boolean>(false);
  const { showToast } = useGlobalToast();
  const [initialLoadError, setInitialLoadError] =
    useState<ApiErrorType | null>(null);
  const [isInitialFetchPending, setIsInitialFetchPending] = useState(
    !state?.repair,
  );
  const isInitialFetchRef = useRef(true);

  useEffect(() => {
    currentDataRef.current = currentData;
  }, [currentData]);

  // AI総合診断の色を決定
  const getOverallColor = (overall: string) => {
    switch (overall) {
      case "適正":
        return "#00A75F";
      case "相場より低い":
      case "相場より圧倒的に低い":
        return "#0B9DBD";
      case "相場より高い":
      case "相場より圧倒的に高い":
        return "#FF6A00";
      default:
        return "#3e3e3e";
    }
  };

  const getOverallBackground = (overall: string) => {
    switch (overall) {
      case "適正":
        return "#bfefcb";
      case "相場より低い":
      case "相場より圧倒的に低い":
        return "#dff5fa";
      case "相場より高い":
      case "相場より圧倒的に高い":
        return "#ffe2cc";
      default:
        return "#f5f5f5";
    }
  };

  type FormValues = {
    title: string;
    areaType: AreaType | "";
    detail: string;
    category: string;
    status: "未対応" | "進行中" | "完了" | "支払い済" | "";
    expenditure_type: "capex" | "opex" | "";
  };

  const defaultValues: FormValues = useMemo(
    () => ({
      title: state?.repair?.title || "",
      areaType: normalizeAreaType(state?.repair?.areaType) as AreaType | "",
      detail: state?.repair?.detail || "",
      category: state?.repair?.category || "",
      status: state?.repair?.status || "",
      expenditure_type: (state?.repair as any)?.expenditure_type || "",
    }),
    [state?.repair],
  );

  const dispatch = useDispatch<any>();
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>(
    initialCategoryOptions,
  );

  // AI チャットアクション完了時 (refetchBus.emit('repair:<id>')) に再フェッチ。
  // この画面は表示を Redux ではなくローカル state (currentData) でやっているため、
  // Redux thunk を呼ぶだけでは画面が更新されない。直接 API を叩いて setCurrentData する。
  const refetchRepair = useCallback(async () => {
    if (!params?.id) return;
    try {
      const BaseURL = import.meta.env.VITE_API_URL;
      const res = await ipponApi.get(`${BaseURL}/api/v1/repairs/${params.id}`);
      const data = res?.data?.data;
      if (data) {
        setCurrentData(data);
        currentDataRef.current = data;
      }
      // Redux にも反映 (他コンポーネントの整合性のため)
      void dispatch(fetchRepairById({ repair_id: params.id }));
    } catch (err) {
      console.error("Error refetching repair:", err);
    }
  }, [dispatch, params?.id]);
  useRefetchOn("repair", params?.id, refetchRepair);

  // 見積選定モーダル: 操作対象の estimate_id を保持。null = 閉。
  const [selectModalEstimateId, setSelectModalEstimateId] = useState<
    string | null
  >(null);
  const [submittingSelect, setSubmittingSelect] = useState(false);
  const { record: categoryRecord } = useSelector(
    (state: RootState) => state.categoryManagement,
  );

  // Fetch category management
  useEffect(() => {
    dispatch(fetchCategoryManagement())
      .unwrap()
      .then((rec: any) => {
        if (rec && Array.isArray(rec.categories)) {
          const mergedValues = Array.from(
            new Set([
              ...initialCategoryOptions.map((o) => o.value),
              ...rec.categories,
            ]),
          );
          setCategoryOptions(
            mergedValues.map((v) => ({ label: String(v), value: String(v) })),
          );
        }
      })
      .catch(() => {
        // 失敗時は初期カテゴリをそのまま使用
      });
  }, [dispatch]);

  // ストアのカテゴリ管理レコードが更新されたら同期
  useEffect(() => {
    if (categoryRecord && Array.isArray(categoryRecord.categories)) {
      const mergedValues = Array.from(
        new Set([
          ...initialCategoryOptions.map((o) => o.value),
          ...categoryRecord.categories,
        ]),
      );
      setCategoryOptions(
        mergedValues.map((v) => ({ label: String(v), value: String(v) })),
      );
    }
  }, [categoryRecord]);

  // Fetch property name when repair data is loaded
  useEffect(() => {
    const fetchPropertyName = async (propertyId: string) => {
      try {
        const BaseURL = import.meta.env.VITE_API_URL;
        const res = await ipponApi.get(
          `${BaseURL}/api/v1/properties/${propertyId}`,
        );
        const propertyData = res?.data?.data;
        if (propertyData && propertyData.name) {
          setPropertyName(propertyData.name);
        }
      } catch (_e) {
        // ignore errors, use fallback
        setPropertyName("物件名");
      }
    };

    if (!propertyName && currentData?.property_id) {
      fetchPropertyName(currentData.property_id);
    }
  }, [currentData?.property_id]);

  // ページ読み込み時に一番上にスクロール
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  // Polling for latest repair status by id (local state; stop when all assessed)
  // リロードや別ページから戻った場合でも、未完了の見積があればポーリングを継続する
  const hadUnassessedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const id =
      overrideRepairId ||
      (params?.id as string) ||
      (state?.repair?.id ? String(state.repair.id) : "");
    if (!id) return;

    const fetchOnce = async () => {
      try {
        const BaseURL = import.meta.env.VITE_API_URL;
        const res = await ipponApi.get(`${BaseURL}/api/v1/repairs/${id}`);
        const data = res?.data?.data;
        if (data) {
          setInitialLoadError(null);
          const pendingReIds = getActiveReEstimatePendingIds(id);
          const pendingReIdsForStaleGuard = new Set(pendingReIds);
          const startedByEid = readReEstimateStore()[id] || {};
          (data.repair_estimates || []).forEach((e: RepairEstimate) => {
            const eid = String(e.repair_estimate_id);
            if (!pendingReIds.has(eid) || !e.ai_assessed) return;
            if (e.ai_error) {
              const started = startedByEid[eid];
              if (
                typeof started === "number" &&
                Date.now() - started < RE_ESTIMATE_STALE_ERROR_GRACE_MS
              ) {
                return;
              }
            }
            clearReEstimatePending(id, eid);
          });
          setReEstimateInProgress(getActiveReEstimatePendingIds(id).size > 0);

          const currentPendingIds = pendingDeleteEstimateIdsRef.current;
          setCurrentData((prev: RepairDetailData) => {
            // 別の修繕に切り替わったら prev の見積を持ち越さず、新しい data 単体を採用。
            // (PreviewDrawer で同じ RepairDetailPage を別 repair_id で再利用すると、
            //  前の見積データが prev に残って新しい修繕の表示に混ざる事象を防ぐ)
            if (
              prev &&
              (prev as any).repair_id &&
              data?.repair_id &&
              (prev as any).repair_id !== data.repair_id
            ) {
              return data as RepairDetailData;
            }
            const newEstimates = (data.repair_estimates ||
              []) as RepairEstimate[];
            const filteredNew =
              currentPendingIds.size > 0
                ? newEstimates.filter(
                    (e) => !currentPendingIds.has(String(e.repair_estimate_id)),
                  )
                : newEstimates;
            const prevEstimates = (prev?.repair_estimates ||
              []) as RepairEstimate[];
            const filteredPrev =
              currentPendingIds.size > 0
                ? prevEstimates.filter(
                    (e) => !currentPendingIds.has(String(e.repair_estimate_id)),
                  )
                : prevEstimates;
            const estimateMap = new Map<string, RepairEstimate>(
              filteredPrev.map((est) => [String(est.repair_estimate_id), est]),
            );
            filteredNew.forEach((est) => {
              const eid = String(est.repair_estimate_id);
              const prevEst = estimateMap.get(eid);
              const apiLooksLikeStaleFailure =
                (est as RepairEstimate).ai_assessed === true &&
                !!(est as RepairEstimate).ai_error;
              const localSaysReassessing =
                prevEst && prevEst.ai_assessed === false && !prevEst.ai_error;
              const sessionSaysPending = pendingReIdsForStaleGuard.has(eid);
              if (apiLooksLikeStaleFailure) {
                if (localSaysReassessing) {
                  return;
                }
                if (sessionSaysPending) {
                  estimateMap.set(
                    eid,
                    applyPreservedRepairImage(prevEst, {
                      ...est,
                      ai_assessed: false,
                      ai_error: undefined,
                    }),
                  );
                  return;
                }
              }
              estimateMap.set(eid, applyPreservedRepairImage(prevEst, est));
            });
            const merged = Array.from(estimateMap.values());
            const mergedRepairImages = mergeRepairImagesPreserveSignedUrls(
              (prev as RepairDetailData)?.repair_images,
              data.repair_images,
            );
            const next = {
              ...data,
              repair_images: mergedRepairImages as typeof data.repair_images,
              repair_estimates: merged,
            };
            const unassessedIds = merged
              .filter((e) => !e.ai_assessed)
              .map((e) => String(e.repair_estimate_id));
            const allAssessed =
              merged.length === 0 || unassessedIds.length === 0;
            if (!allAssessed) {
              for (const uid of unassessedIds)
                hadUnassessedIdsRef.current.add(uid);
              // 未完了 estimate がある時だけ polling を起動 (= 起動済みなら継続)。
              // unmount 後に start しないよう unmountedRef も確認。
              if (!pollingRef.current && !unmountedRef.current) {
                pollingRef.current = setInterval(fetchOnce, 4000);
              }
            } else if (allAssessed && pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              const tracked = hadUnassessedIdsRef.current;
              if (tracked.size > 0) {
                const hasErrors = merged.some(
                  (e) =>
                    tracked.has(String(e.repair_estimate_id)) && !!e.ai_error,
                );
                hadUnassessedIdsRef.current = new Set();
                if (hasErrors) {
                  showToast({
                    message:
                      "見積書の解析に失敗しました。再度アップロードしてください。",
                    type: "error",
                  });
                } else {
                  showToast({
                    message: "見積書の解析が完了しました",
                    type: "success",
                  });
                }
              }
            }
            return next;
          });
        }
      } catch (e) {
        // ポーリング中の transient エラーは表示を維持するため、初回 fetch のみ反映
        if (isInitialFetchRef.current) {
          setInitialLoadError(getApiErrorType(e));
        }
      } finally {
        if (isInitialFetchRef.current) {
          isInitialFetchRef.current = false;
          setIsInitialFetchPending(false);
        }
      }
    };

    // 初回のみ即時実行。setInterval は fetchOnce 内で
    // 「未完了 estimate あり かつ未起動」のときだけ起動する (= 無駄なポーリング防止)。
    unmountedRef.current = false;
    fetchOnceRef.current = fetchOnce;
    fetchOnce();
    return () => {
      unmountedRef.current = true;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [overrideRepairId, params?.id, state?.repair?.id]);
  const repairImages = currentData?.repair_images || [];

  const {
    register,
    control,
    formState: { errors, isSubmitting, dirtyFields },
    handleSubmit,
    watch,
    setValue,
    reset,
  } = useForm<FormValues>({
    defaultValues,
    mode: "onChange",
  });
  const scrollToFirstError = useScrollToFirstError<FormValues>();
  const registerAny = register as any;
  const errorsAny = errors as any;
  const watchAny = watch as any;
  const leftInputContainerWidth = "600px";
  const labelWidth = "120px";

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [imagesChanged, setImagesChanged] = useState(false);
  const [estimateImagesChanged, setEstimateImagesChanged] = useState(false);
  const [analyzingEstimates, setAnalyzingEstimates] = useState(false);
  const [reEstimateInProgress, setReEstimateInProgress] = useState(false);

  // 名称セレクトの動的 options (modal と同じ仕様)。value=landmark_name、unit_id は別保持
  const [areaOptions, setAreaOptions] = useState<
    { label: string; value: string; unit_id: string }[]
  >([]);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);
  const [targetUnitId, setTargetUnitId] = useState<string | undefined>(
    undefined,
  );

  // 物件配下の Unit 一覧 (area_type 問わず) を Redux 経由で取得。
  // モーダル側 (RepairWorkCreateModal) と挙動を揃え、currentData?.property_id が
  // 後から確定するレース問題を避けるため API 直叩きはしない。
  // useSelector で `|| []` を直接返すと毎レンダー新しい配列参照になって useEffect が
  // 無駄に走るため、ここでは undefined を取って useMemo で安定化させる。
  const unitsData = useSelector(
    (s: RootState) => (s as any).units?.list?.data as UnitRecord[] | undefined,
  );
  const allUnits = useMemo(() => unitsData ?? [], [unitsData]);
  const equipmentList = useMemo(
    () => allUnits.filter((u) => u.area_type === "facility"),
    [allUnits],
  );

  const watchedAreaType = watchAny("areaType") as AreaType | "";

  // 既存修繕の target_unit_id を state に同期
  useEffect(() => {
    const initialUnitId = (currentData as any)?.target_unit_id;
    if (initialUnitId) setTargetUnitId(String(initialUnitId));
  }, [currentData]);

  // property_id 確定したら一度だけ Unit 一覧を取得 (建物設備も含む全件)
  useEffect(() => {
    const propertyId = currentData?.property_id;
    if (!propertyId) return;
    setIsLoadingAreas(true);
    Promise.resolve(
      dispatch(fetchUnitsByProperty({ property_id: propertyId }) as any),
    ).finally(() => setIsLoadingAreas(false));
  }, [currentData?.property_id, dispatch]);

  // areaOptions は Redux 上の Unit を watchedAreaType でフィルタして導出。
  // facility は equipmentList を使う既存ロジックを温存しつつ、その他の area も
  // 同じソース (Redux) から拾う形にすることで「区画情報がありません」を解消する。
  useEffect(() => {
    if (!watchedAreaType || watchedAreaType === "facility") {
      setAreaOptions([]);
      return;
    }
    const filtered = allUnits.filter((u) => u.area_type === watchedAreaType);
    setAreaOptions(
      filtered.map((u) => ({
        label: String(u.landmark_name || ""),
        value: String(u.landmark_name || ""),
        unit_id: String(u.unit_id || ""),
      })),
    );
  }, [allUnits, watchedAreaType]);
  const [pendingEstimateCount, setPendingEstimateCount] = useState(0);
  const [estimateUploaderKey, setEstimateUploaderKey] = useState(0);
  const [pendingDeleteEstimateIds, setPendingDeleteEstimateIds] = useState<
    Set<string>
  >(new Set());
  const pendingDeleteEstimateIdsRef = useRef<Set<string>>(new Set());

  const repairEstimates = React.useMemo(() => {
    const estimates = (currentData?.repair_estimates || []) as RepairEstimate[];
    // 削除予定の見積を除外
    const filtered = estimates.filter(
      (estimate) =>
        !pendingDeleteEstimateIds.has(String(estimate.repair_estimate_id)),
    );
    return filtered;
  }, [currentData?.repair_estimates, pendingDeleteEstimateIds]);
  const initialCategoriesRef = useRef<string[]>([]);
  const initialRepairImagesRef = useRef<string[]>([]);
  const processingEstimateIdsRef = useRef<Set<string>>(new Set());

  // カテゴリ追加/削除ハンドラ（詳細画面でもモーダルと同様に追加可能にする）
  const handleAddNewCategory = useCallback(
    (newCategory: string) => {
      const trimmed = String(newCategory || "").trim();
      if (!trimmed) return;
      // 既存一致なら選択に追加のみ
      const exists = categoryOptions.some(
        (o) => o.value.toLowerCase() === trimmed.toLowerCase(),
      );
      if (exists) {
        setSelectedCategories((prev) => {
          if (prev.includes(trimmed)) return prev;
          const next = [...prev, trimmed];
          setValue("category" as any, next.join(", ") as any, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          return next;
        });
        return;
      }
      // 新規オプションとして追加し、選択にも加える
      const newOption: CategoryOption = { label: trimmed, value: trimmed };
      setCategoryOptions((prev) => [...prev, newOption]);
      setSelectedCategories((prev) => {
        const next = [...prev, trimmed];
        setValue("category" as any, next.join(", ") as any, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        return next;
      });
    },
    [categoryOptions, setValue],
  );

  const handleDeleteCategoryOption = useCallback(
    (categoryValue: string) => {
      setCategoryOptions((prev) =>
        prev.filter((o) => o.value !== categoryValue),
      );
      setSelectedCategories((prev) => {
        if (!prev.includes(categoryValue)) return prev;
        const next = prev.filter((c) => c !== categoryValue);
        setValue("category" as any, next.join(", ") as any, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        return next;
      });
    },
    [setValue],
  );

  const normalizeImagePath = useCallback((value: string) => {
    if (!value) return "";
    try {
      const parsed = new URL(value);
      const decodedPath = decodeURIComponent(parsed.pathname);
      return parsed.protocol + "//" + parsed.host + decodedPath;
    } catch (_error) {
      const base = value.split("?")[0];
      try {
        return decodeURIComponent(base);
      } catch {
        return base;
      }
    }
  }, []);

  const handleRepairImagesDeleted = useCallback((_paths: string[]) => {
    setImagesChanged(true);
  }, []);

  const handleEstimateImagesDeleted = useCallback((_paths: string[]) => {
    setEstimateImagesChanged(true);
  }, []);

  const locationRepair = state?.repair;

  const resolvedRepairId = useMemo(() => {
    const isValidId = (id: string) => /^[a-zA-Z0-9_-]+$/.test(id);
    // ドロワー等から明示指定があれば最優先
    if (overrideRepairId && isValidId(overrideRepairId))
      return overrideRepairId;
    if (params?.id && isValidId(params.id)) return params.id;
    if (state?.repair?.id) {
      const id = String(state.repair.id);
      if (isValidId(id)) return id;
    }
    if (currentData?.repair_id) {
      const id = String(currentData.repair_id);
      if (isValidId(id)) return id;
    }
    return "";
  }, [overrideRepairId, params?.id, state?.repair?.id, currentData?.repair_id]);

  useLayoutEffect(() => {
    const id = resolvedRepairId;
    if (!id) {
      setReEstimateInProgress(false);
      return;
    }
    const pending = getActiveReEstimatePendingIds(id);
    if (pending.size === 0) {
      setReEstimateInProgress(false);
      return;
    }
    setReEstimateInProgress(true);
    setCurrentData((prev: RepairDetailData | null) => {
      if (!prev?.repair_estimates?.length) return prev;
      let changed = false;
      const nextEst = prev.repair_estimates.map((e) => {
        if (!pending.has(String(e.repair_estimate_id))) return e;
        if (e.ai_assessed === false && !e.ai_error) return e;
        changed = true;
        return { ...e, ai_assessed: false, ai_error: undefined };
      });
      return changed ? { ...prev, repair_estimates: nextEst } : prev;
    });
  }, [resolvedRepairId]);

  // mount 時の自動 analyze キックは廃止。
  // 見積アップロード時に BE 側で invokeRepairEstimateIngestLambda が裏側で
  // OCR/解析を起動するため、フロントで再キックする必要はない (= 二重実行になる)。
  // ローカル環境で Lambda が起動しない場合のフォールバックは、必要に応じて
  // 手動「再解析」ボタンや別の仕組みで対応する。

  const handleEstimateImagesChange = useCallback(() => {
    setEstimateImagesChanged(true);
  }, []);

  // 見積画像が追加されたときに 1 ファイルずつアップロードし、fileId 単位でポーリング
  useEffect(() => {
    const processEstimateImages = async () => {
      const estimateImagesValue = watchAny("estimate_images");
      if (
        !Array.isArray(estimateImagesValue) ||
        estimateImagesValue.length === 0
      ) {
        setEstimateImagesChanged(false);
        return;
      }

      const queue: any[] = [];
      const idsToProcess: string[] = [];

      estimateImagesValue.forEach((item: any) => {
        const candidateId =
          item?.id ||
          item?.originalFileName ||
          item?.name ||
          item?.fileName ||
          item?.filename ||
          item?.data ||
          item?.base64;
        if (!candidateId) return;
        const key = String(candidateId);
        if (processingEstimateIdsRef.current.has(key)) return;
        queue.push(item);
        idsToProcess.push(key);
      });

      if (queue.length === 0) return;

      const targetId = resolvedRepairId;
      if (!targetId) {
        showToast({ message: "工事IDを特定できません", type: "error" });
        return;
      }

      const formatted = queue
        .map((item: any) => {
          const dataValue =
            typeof item?.data === "string"
              ? item.data
              : typeof item?.base64 === "string"
                ? item.base64
                : null;
          if (!dataValue) return null;
          return {
            data: dataValue,
            originalFileName:
              item?.originalFileName ||
              item?.name ||
              item?.fileName ||
              item?.filename ||
              undefined,
            mimeType: item?.mimeType || item?.type || undefined,
          };
        })
        .filter(Boolean) as Array<{
        data: string;
        originalFileName?: string;
        mimeType?: string;
      }>;

      if (formatted.length === 0) {
        setEstimateImagesChanged(false);
        return;
      }

      const normalizeFileName = (s: string) =>
        (s ?? "")
          .trim()
          .replace(/^.*[/\\]/, "")
          .toLowerCase();

      const existingEstimates = (currentData?.repair_estimates || []).filter(
        (e: any) =>
          !pendingDeleteEstimateIdsRef.current.has(
            String(e?.repair_estimate_id ?? ""),
          ),
      );
      const existingFileNames = new Set(
        existingEstimates
          .map((e: any) => normalizeFileName(e?.file_name ?? ""))
          .filter(Boolean),
      );

      const toUpload: typeof formatted = [];
      for (const item of formatted) {
        const name = normalizeFileName(item.originalFileName ?? "");
        if (!name || !existingFileNames.has(name)) {
          toUpload.push(item);
        }
      }

      const remainingItems = (estimateImagesValue as any[]).filter(
        (item: any) => {
          const name = normalizeFileName(
            item?.originalFileName ??
              item?.name ??
              item?.fileName ??
              item?.filename ??
              "",
          );
          return !(name && existingFileNames.has(name));
        },
      );

      const hasDuplicates = toUpload.length < formatted.length;
      if (hasDuplicates) {
        showToast({
          message: "同じファイルはアップロードできません",
          type: "info",
        });
        setValue("estimate_images" as any, remainingItems, {
          shouldDirty: false,
        });
        setEstimateUploaderKey((k) => k + 1);
      }

      if (toUpload.length === 0) {
        setEstimateImagesChanged(false);
        idsToProcess.forEach((id) =>
          processingEstimateIdsRef.current.delete(id),
        );
        return;
      }

      setEstimateImagesChanged(false);
      const idsToProcessForRef = toUpload.map((item) =>
        String(item.originalFileName ?? item.data?.slice?.(0, 50) ?? "").slice(
          0,
          100,
        ),
      );
      idsToProcessForRef.forEach((id) =>
        processingEstimateIdsRef.current.add(id),
      );
      const pendingCount = toUpload.length;
      if (pendingCount > 0) {
        setPendingEstimateCount((prev) => prev + pendingCount);
      }
      setAnalyzingEstimates(true);

      const fileIds: string[] = [];
      for (const item of toUpload) {
        try {
          const result = await dispatch(
            uploadRepairEstimate({
              repair_id: targetId,
              estimate_image: item,
            }),
          ).unwrap();
          if (result?.fileId) fileIds.push(result.fileId);
        } catch (error) {
          // AI を止めた 403（トライアルの無料枠を使い切った・解約後の猶予期間）は、理由を出す
          showToast({
            message:
              resolveAiBlockedMessage(error) ??
              "見積書のアップロードに失敗しました",
            type: "error",
          });
        }
      }

      if (fileIds.length === 0) {
        setAnalyzingEstimates(false);
        idsToProcess.forEach((id) =>
          processingEstimateIdsRef.current.delete(id),
        );
        if (pendingCount > 0) {
          setPendingEstimateCount((prev) => Math.max(0, prev - pendingCount));
        }
        return;
      }

      showToast({ message: "見積書をアップロードしました。", type: "success" });
      setValue("estimate_images" as any, [], { shouldDirty: false });

      // 解析は BE 側で upload 時に invokeRepairEstimateIngestLambda が裏側で
      // 起動するため、FE からの analyze-existing 再キックは廃止 (= 二重実行防止)。
      // 完了検知は後続の独自ポーリングで ai_assessed=true を待つ。

      const BaseURL = import.meta.env.VITE_API_URL;
      let attempts = 0;
      const maxAttempts = 40;
      const pollInterval = 3000;
      const completedFileIds = new Set<string>();

      const mergeSingleEstimate = (prev: any, estimate: any) => {
        if (!estimate || estimate.repair_estimate_id == null) return prev;
        const currentEstimates = [...(prev?.repair_estimates || [])];
        const id = String(estimate.repair_estimate_id);
        const idx = currentEstimates.findIndex(
          (e: any) => String(e?.repair_estimate_id) === id,
        );
        if (idx >= 0) {
          currentEstimates[idx] = estimate;
        } else {
          currentEstimates.push(estimate);
        }
        return { ...prev, repair_estimates: currentEstimates };
      };

      const mergeOnlyNewEstimates = (
        prev: any,
        repairData: any,
        newFileIds: string[],
      ) => {
        const fileIdSet = new Set(newFileIds);
        const currentEstimates = (prev?.repair_estimates ||
          []) as RepairEstimate[];
        const refreshedEstimates = (repairData?.repair_estimates ||
          []) as RepairEstimate[];

        const otherEstimates = currentEstimates.filter(
          (e) => !fileIdSet.has(String(e.repair_estimate_id)),
        );
        const newAndUpdatedEstimates = refreshedEstimates.filter((e) =>
          fileIdSet.has(String(e.repair_estimate_id)),
        );

        const merged = [...otherEstimates, ...newAndUpdatedEstimates];
        return { ...repairData, repair_estimates: merged };
      };

      const finalizePolling = async () => {
        let allNewAssessed = false;
        try {
          const refreshed = await ipponApi.get(
            `${BaseURL}/api/v1/repairs/${targetId}`,
          );
          const repairData = refreshed.data?.data ?? refreshed.data;
          setCurrentData((prev: RepairDetailData) =>
            mergeOnlyNewEstimates(prev, repairData, fileIds),
          );
          // アップロードした見積書の解析完了状況を確認
          const newEstimates = (
            (repairData?.repair_estimates || []) as RepairEstimate[]
          ).filter((e) => fileIds.includes(String(e.repair_estimate_id)));
          allNewAssessed =
            newEstimates.length > 0 &&
            newEstimates.every((e) => !!e.ai_assessed);
          if (allNewAssessed) {
            const hasErrors = newEstimates.some((e) => !!e.ai_error);
            if (!hasErrors) {
              showToast({
                message: "見積書の解析が完了しました",
                type: "success",
              });
            }
            // エラーのトーストは個別ポーリング時(pollForCompletion)で既に表示済み
            // バックグラウンドポーリング側の二重 toast 防止
            hadUnassessedIdsRef.current = new Set();
          }
        } catch (_e) {
          // 最終取得に失敗しても state はクリーンアップする
        }
        setPendingEstimateCount(0);
        setAnalyzingEstimates(false);
        processingEstimateIdsRef.current.clear();
        // 解析がまだ完了していない場合、バックグラウンドポーリングを再開して完了を検知する
        if (!allNewAssessed && !pollingRef.current && fetchOnceRef.current) {
          const originalFetchOnce = fetchOnceRef.current;
          const wrappedFetchOnce = async () => {
            await originalFetchOnce();
            // fetchOnce 内で allAssessed=true を検知するとポーリングを停止する
            // その停止を検知する（トーストはfetchOnce内で表示済み）
          };
          pollingRef.current = setInterval(wrappedFetchOnce, 4000);
        }
      };

      const pollForCompletion = async () => {
        attempts++;
        try {
          for (const fileId of fileIds) {
            if (completedFileIds.has(fileId)) continue;
            const res = await ipponApi.get(
              `${BaseURL}/api/v1/repairs/${targetId}/estimates/${fileId}`,
            );
            const est = (res.data?.data ?? res.data) as
              | RepairEstimate
              | undefined;
            if (est?.ai_assessed) {
              completedFileIds.add(fileId);
              setCurrentData((prev: RepairDetailData) =>
                mergeSingleEstimate(prev, est),
              );
              setPendingEstimateCount((p: number) => Math.max(0, p - 1));
              if (est.ai_error) {
                showToast({
                  message:
                    "見積書の解析に失敗しました。再度アップロードしてください。",
                  type: "error",
                });
              }
            }
          }

          if (completedFileIds.size === fileIds.length) {
            const refreshed = await ipponApi.get(
              `${BaseURL}/api/v1/repairs/${targetId}`,
            );
            const repairData = refreshed.data?.data ?? refreshed.data;
            setCurrentData((prev: RepairDetailData) =>
              mergeOnlyNewEstimates(prev, repairData, fileIds),
            );
            processingEstimateIdsRef.current.clear();
            const errorEstimates = (
              (repairData?.repair_estimates || []) as RepairEstimate[]
            ).filter(
              (e) =>
                fileIds.includes(String(e.repair_estimate_id)) && e.ai_error,
            );
            if (errorEstimates.length === 0) {
              showToast({
                message: "見積書の解析が完了しました",
                type: "success",
              });
            }
            // バックグラウンドポーリング (fetchOnce) 側が次回ループで
            // 同じ完了検知をして二重 toast にならないよう、追跡 ID を消費する
            hadUnassessedIdsRef.current = new Set();
            setAnalyzingEstimates(false);
            return;
          }

          if (attempts < maxAttempts) {
            setTimeout(pollForCompletion, pollInterval);
          } else {
            await finalizePolling();
          }
        } catch (error) {
          console.error("Polling error:", error);
          if (attempts < maxAttempts) {
            setTimeout(pollForCompletion, pollInterval);
          } else {
            await finalizePolling();
            showToast({
              message: "解析状況の確認に失敗しました",
              type: "error",
            });
          }
        }
      };

      setTimeout(pollForCompletion, pollInterval);
    };

    if (estimateImagesChanged) {
      const timer = setTimeout(() => processEstimateImages(), 0);
      return () => clearTimeout(timer);
    }
  }, [
    estimateImagesChanged,
    dispatch,
    resolvedRepairId,
    setValue,
    showToast,
    watchAny,
  ]);

  const handleDeleteEstimate = useCallback((estimateId: string) => {
    if (!estimateId) return;
    setPendingDeleteEstimateIds((prev) => {
      const next = new Set(prev).add(estimateId);
      pendingDeleteEstimateIdsRef.current = next;
      return next;
    });
  }, []);

  const handleReEstimate = useCallback(
    async (estimateId: string) => {
      const targetId = resolvedRepairId;
      if (!targetId || !estimateId) {
        showToast({
          message: "再見積もりに必要な情報が不足しています",
          type: "error",
        });
        return;
      }

      markReEstimatePending(targetId, estimateId);

      // UIを解析中状態に戻す
      setReEstimateInProgress(true);
      setCurrentData((prev: any) => ({
        ...prev,
        repair_estimates: (prev?.repair_estimates || []).map((e: any) =>
          String(e.repair_estimate_id) === String(estimateId)
            ? { ...e, ai_assessed: false, ai_error: undefined }
            : e,
        ),
      }));

      markEstimateKicked([String(estimateId)]);
      try {
        await dispatch(
          analyzeExistingRepairEstimates({
            repair_id: targetId,
            estimate_id: estimateId,
          }),
        ).unwrap();

        // ポーリングで結果を待つ
        const BaseURL = import.meta.env.VITE_API_URL;
        let attempts = 0;
        const maxAttempts = 40;
        const pollInterval = 3000;

        const pollForReEstimate = async () => {
          attempts++;
          try {
            const res = await ipponApi.get(
              `${BaseURL}/api/v1/repairs/${targetId}/estimates/${estimateId}`,
            );
            const est = (res.data?.data ?? res.data) as
              | RepairEstimate
              | undefined;

            if (est?.ai_assessed) {
              try {
                setCurrentData((prev: any) => ({
                  ...prev,
                  repair_estimates: (prev?.repair_estimates || []).map(
                    (e: any) =>
                      String(e.repair_estimate_id) === String(estimateId)
                        ? est
                        : e,
                  ),
                }));

                if (est.ai_error) {
                  showToast({
                    message: "再見積もりに失敗しました",
                    type: "error",
                  });
                } else {
                  showToast({
                    message: "再見積もりが完了しました",
                    type: "success",
                  });
                }
              } finally {
                clearReEstimatePending(targetId, estimateId);
                setReEstimateInProgress(false);
              }
              return;
            }

            if (attempts < maxAttempts) {
              setTimeout(pollForReEstimate, pollInterval);
            } else {
              try {
                const refreshed = await ipponApi.get(
                  `${BaseURL}/api/v1/repairs/${targetId}`,
                );
                const repairData = refreshed.data?.data ?? refreshed.data;
                setCurrentData(repairData);
                showToast({
                  message:
                    "再見積もりがタイムアウトしました。しばらくしてから確認してください。",
                  type: "error",
                });
              } finally {
                clearReEstimatePending(targetId, estimateId);
                setReEstimateInProgress(false);
              }
            }
          } catch (error) {
            if (attempts < maxAttempts) {
              setTimeout(pollForReEstimate, pollInterval);
            } else {
              try {
                showToast({
                  message: "再見積もり中にエラーが発生しました",
                  type: "error",
                });
              } finally {
                clearReEstimatePending(targetId, estimateId);
                setReEstimateInProgress(false);
              }
            }
          }
        };

        setTimeout(pollForReEstimate, pollInterval);
      } catch (_e) {
        try {
          showToast({
            message: "再見積もりの実行に失敗しました",
            type: "error",
          });
          setCurrentData((prev: any) => ({
            ...prev,
            repair_estimates: (prev?.repair_estimates || []).map((e: any) =>
              String(e.repair_estimate_id) === String(estimateId)
                ? { ...e, ai_assessed: true, ai_error: "re_estimate_failed" }
                : e,
            ),
          }));
        } finally {
          clearReEstimatePending(targetId, estimateId);
          setReEstimateInProgress(false);
        }
      }
    },
    [resolvedRepairId, dispatch, showToast],
  );

  // 初回ロード時のみ form を reset する (polling で currentData が更新されても再 reset しない)
  const hasInitializedFormRef = useRef<string>("");
  useEffect(() => {
    const source: any = currentData ?? locationRepair;
    if (!source) return;
    if (estimateImagesChanged) return;

    const sourceId = String(
      source.repair_id ?? source.id ?? resolvedRepairId ?? "",
    );
    if (!sourceId) return;
    if (hasInitializedFormRef.current === sourceId) return;
    hasInitializedFormRef.current = sourceId;

    const rawCategory = source.category as string | string[] | undefined;
    const nextCategories = Array.isArray(rawCategory)
      ? rawCategory
      : typeof rawCategory === "string"
        ? rawCategory
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean)
        : [];

    initialCategoriesRef.current = nextCategories;
    initialRepairImagesRef.current = Array.isArray(source.repair_images)
      ? [...source.repair_images]
      : [];
    setSelectedCategories(nextCategories);

    const currentEstimateImages = watchAny("estimate_images");
    const preserveEstimateImages =
      Array.isArray(currentEstimateImages) && currentEstimateImages.length > 0;

    reset({
      title: source.repair_title ?? source.title ?? "",
      // legacy データで area_type に日本語ラベルが入っているケースを吸収
      areaType: normalizeAreaType(
        source.area_type ?? source.areaType,
      ) as FormValues["areaType"],
      detail: source.area_name ?? source.detail ?? "",
      category: nextCategories.join(", "),
      status: source.status ?? "",
      expenditure_type: source.expenditure_type ?? "",
      ...(preserveEstimateImages
        ? { estimate_images: currentEstimateImages }
        : {}),
    } as FormValues);

    setImagesChanged(false);
  }, [
    currentData,
    locationRepair,
    reset,
    estimateImagesChanged,
    watchAny,
    resolvedRepairId,
  ]);

  const onSubmit = async (formData: FormValues) => {
    const id =
      overrideRepairId ||
      (params?.id as string) ||
      (state?.repair?.id ? String(state.repair.id) : "");
    if (!id) return;
    try {
      const payload: UpdateRepairPayload = { repair_id: id };

      const statusValue =
        formData.status || currentData?.status || state?.repair?.status;
      if (statusValue) {
        payload.status = statusValue;
      }

      const parsedCategories = (formData.category || "")
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);

      if (selectedCategories.length > 0) {
        payload.category = selectedCategories;
      } else if (parsedCategories.length > 0) {
        payload.category = parsedCategories;
      } else if (initialCategoriesRef.current.length > 0) {
        payload.category = [];
      }

      const repairTitle = formData.title?.trim();
      if (repairTitle) {
        payload.repair_title = repairTitle;
      }

      if (formData.areaType) {
        payload.area_type = formData.areaType;
      }

      if (formData.expenditure_type) {
        payload.expenditure_type = formData.expenditure_type as
          | "capex"
          | "opex";
      }

      const areaName = formData.detail?.trim();
      if (areaName) {
        payload.area_name = areaName;
        payload.target_name_snapshot = areaName;
      }
      if (targetUnitId) {
        payload.target_unit_id = targetUnitId;
      }

      // 画像が変更された場合のみ画像関連の処理を実行
      if (imagesChanged) {
        const repairImages = watchAny("images");
        const originalImages = Array.isArray(initialRepairImagesRef.current)
          ? initialRepairImagesRef.current
          : [];

        const originalLookup = new Map<string, string>();
        originalImages.forEach((url) => {
          if (typeof url !== "string" || !url.trim()) return;
          originalLookup.set(normalizeImagePath(url), url);
        });

        const remainingNormalized = new Set<string>();
        const newImages: Array<{
          data: string;
          originalFileName?: string;
          mimeType?: string;
        }> = [];

        if (Array.isArray(repairImages)) {
          repairImages.forEach((item: any) => {
            let dataValue: string | undefined;
            let originalFileName: string | undefined;
            let mimeType: string | undefined;

            if (typeof item === "string") {
              dataValue = item;
            } else if (item && typeof item === "object") {
              dataValue =
                item.data ||
                item.base64 ||
                item.public_file_path ||
                item.url ||
                item.path ||
                "";
              originalFileName =
                item.originalFileName ||
                item.name ||
                item.fileName ||
                item.filename;
              mimeType = item.mimeType || item.type;
            }

            if (!dataValue || typeof dataValue !== "string") {
              return;
            }

            if (dataValue.startsWith("http")) {
              remainingNormalized.add(normalizeImagePath(dataValue));
              return;
            }

            const formatted: {
              data: string;
              originalFileName?: string;
              mimeType?: string;
            } = {
              data: dataValue,
            };
            if (originalFileName) formatted.originalFileName = originalFileName;
            if (mimeType) formatted.mimeType = mimeType;
            newImages.push(formatted);
          });
        }

        if (newImages.length > 0) {
          payload.repair_images = newImages;
        }

        const removedNormalized = Array.from(originalLookup.keys()).filter(
          (key) => !remainingNormalized.has(key),
        );

        if (removedNormalized.length > 0) {
          payload.remove_repair_images = removedNormalized.map(
            (key) => originalLookup.get(key) || key,
          );
        }
      }

      const successfullyDeletedIds: string[] = [];

      if (pendingDeleteEstimateIds.size > 0) {
        const failedDeletes: { estimateId: string; error: any }[] = [];

        for (const estimateId of Array.from(pendingDeleteEstimateIds)) {
          try {
            await dispatch(
              deleteRepairEstimate({
                repair_id: id,
                repair_estimate_id: estimateId,
              }),
            ).unwrap();
            successfullyDeletedIds.push(estimateId);
          } catch (error) {
            console.error(`Failed to delete estimate ${estimateId}`, error);
            failedDeletes.push({ estimateId, error });
          }
        }

        if (failedDeletes.length > 0) {
          showToast({
            message: `${failedDeletes.length}件の見積書の削除に失敗しました`,
            type: "error",
          });
        }
      }

      const res: any = await dispatch(updateRepair(payload as any));
      if ((updateRepair as any).fulfilled.match(res)) {
        let updated = res.payload;

        // 削除に成功した見積がレスポンスに含まれていたら除外する
        if (updated && successfullyDeletedIds.length > 0) {
          updated = {
            ...updated,
            repair_estimates: Array.isArray(updated.repair_estimates)
              ? updated.repair_estimates.filter(
                  (e: any) =>
                    !successfullyDeletedIds.includes(
                      String(e.repair_estimate_id),
                    ),
                )
              : [],
          };
        }

        if (updated) {
          // 更新成功時は form を新しい値で reset して dirty 状態を解除する。
          // (= 更新ボタンの非活性化、ページ遷移確認モーダルの抑止)
          // hasInitializedFormRef をクリアすると、setCurrentData による
          // currentData 変更で useEffect が再走り、reset({...}) が当たる。
          hasInitializedFormRef.current = "";
          setCurrentData(updated);
          setImagesChanged(false);
          setEstimateImagesChanged(false);

          if (successfullyDeletedIds.length > 0) {
            setPendingDeleteEstimateIds((prev) => {
              const next = new Set(prev);
              successfullyDeletedIds.forEach((id) => next.delete(id));
              pendingDeleteEstimateIdsRef.current = next;
              return next;
            });
          }
          showToast({ message: "工事情報を更新しました", type: "success" });

          // カテゴリ管理へも反映（新規カテゴリがあればアップサート）
          try {
            const rec = await dispatch(fetchCategoryManagement()).unwrap();
            const existing: string[] = Array.isArray(rec?.categories)
              ? rec!.categories
              : [];
            const next = Array.from(
              new Set([...(existing || []), ...(selectedCategories || [])]),
            ).filter(Boolean);
            if (next.length !== existing.length) {
              await dispatch(
                upsertCategoryManagement({
                  existingId: rec?.category_management_id,
                  categories: next,
                }) as any,
              )
                .unwrap()
                .catch(() => {});
            }
          } catch (_e) {
            // 失敗しても本体の更新は成功しているため無視
          }
        }
      } else {
        // 見積書を付けた更新は、AI を止めた 403（トライアルの無料枠を使い切った・解約後の猶予期間）で断られる
        showToast({
          message: resolveAiBlockedMessage(res?.payload) ?? "更新に失敗しました",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Update error:", error);
      showToast({ message: "更新中にエラーが発生しました", type: "error" });
    }
  };

  useEffect(() => {
    const repairTitle =
      currentData?.repair_title ||
      currentData?.title ||
      state?.repair?.title ||
      (state as any)?.customerName ||
      "";
    if (!repairTitle) return;

    // 埋め込み時 (ドロワー) は URL を書き換えない
    if (overrideRepairId) return;

    const expectedPropName = propertyName || undefined;
    const expectedPropertyId = currentData?.property_id;
    const currentCustomerName = (state as any)?.customerName;
    const currentPropName = (state as any)?.propName;
    const currentPropertyId = (state as any)?.propertyId;

    // state が既に正しければ no-op (popstate 等で state が消えたら再設定する)
    if (
      currentCustomerName === repairTitle &&
      currentPropName === expectedPropName &&
      currentPropertyId === expectedPropertyId
    ) {
      return;
    }

    const nextState: Record<string, unknown> = state ? { ...state } : {};
    if ("showToast" in nextState) {
      delete (nextState as any).showToast;
    }

    navigate(`/repairs/${params?.id}`, {
      state: {
        ...nextState,
        propName: expectedPropName,
        customerName: repairTitle,
        propertyId: expectedPropertyId,
      },
      replace: true,
    });
  }, [
    propertyName,
    currentData?.repair_title,
    currentData?.title,
    currentData?.property_id,
    state,
    navigate,
    params?.id,
    overrideRepairId,
  ]);

  // 見積画像 (estimate_images) はアップロード時点で BE 側に自動保存されるため、
  // フォームの「未保存変更」扱いにはしない (ナビゲーションガード対象外)。
  //   - estimateImagesChanged フラグも除外
  //   - RHF の dirtyFields からも estimate_images キーを除外
  const isFormChanged =
    Object.keys(dirtyFields).some((k) => k !== "estimate_images") ||
    imagesChanged ||
    pendingDeleteEstimateIds.size > 0;

  // PreviewDrawer (工事詳細プレビュー) に埋め込まれた embedded モード時、編集中(dirty)を
  // 親 PreviewDrawer に報告し、閉じ操作で破棄確認を出させる。
  // PreviewDrawer 外 (フルページ) では no-op (フルページは CustomFormNavigationGuard が担当)。
  usePreviewDrawerUnsaved(isFormChanged);

  const handleInitialLoadRetry = useCallback(() => {
    setInitialLoadError(null);
    setIsInitialFetchPending(true);
    isInitialFetchRef.current = true;
    fetchOnceRef.current?.();
  }, []);

  const hasRepairData = Boolean(currentData);
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasRepairData,
    dedupeKey: `repair:${overrideRepairId || params?.id || ""}`,
  });

  if (shouldShowInitialLoadError(initialLoadError, hasRepairData)) {
    return (
      <InitialLoadError
        errorType={initialLoadError}
        onRetry={handleInitialLoadRetry}
      />
    );
  }

  if (!currentData) {
    if (isInitialFetchPending) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CustomLoading loading={true} fullScreen={false} size="medium" />
        </Box>
      );
    }
  }

  return (
    <CustomFormNavigationGuard
      hasFormChanged={isFormChanged}
      isSubmitting={isSubmitting}
    >
      {() => (
        <Box
          pb={9}
          // pt は sticky タイトルの「外」に置くとスクロール時に動くので常に 0。
          // 上余白は sticky タイトル Box の内側 pt で持たせる。
          pt={0}
          // ドロワー時は PreviewDrawer の px(12) + ここ(12) = 24 で全画面(単独ページ)と揃える。
          // 単独ページは 0 (DashboardLayout の pl/pr=24 が効く)。
          px={embedded ? 1.5 : 0}
          data-entity="repair"
          data-id={params?.id || "new"}
        >
          {/* 画面コンテキスト用 (pageContext.ts 参照): 物件名と工事案件の親 ID。
              画面表示は CustomSectionTitle 側が担うので、ここでは hidden 要素として
              data-field のみ提供する。 */}
          <span
            data-field="property_name"
            data-label="物件名"
            style={{ display: "none" }}
          >
            {propertyName}
          </span>
          <span
            data-field="property_id"
            data-label="物件ID"
            style={{ display: "none" }}
          >
            {currentData?.property_id || ""}
          </span>
          <Box
            sx={{
              mb: 3,
              // タイトルを上端に sticky 固定。ドロワー(embedded)はスクロール先頭(0)、
              // 単独ページ(全画面)は固定ヘッダー(50px)の下に貼り付ける。
              // 上余白は sticky 要素の内側 pt で持たせる (外側だとスクロール時に動く)。
              pt: embedded ? 0 : 0.5,
              position: "sticky",
              top: embedded ? 0 : 50,
              zIndex: 10,
              backgroundColor: "white",
            }}
          >
            <CustomSectionTitle
              title={
                currentData?.repair_title ||
                currentData?.title ||
                state?.repair?.title ||
                (state as any)?.customerName ||
                ""
              }
              size="large"
              addBorder
              icon={!embedded ? <BackButton size={24} /> : undefined}
              rightContent={
                <Box
                  sx={{
                    width: 160,
                    "& .inputLabel": { display: "none" },
                    "& > *": { margin: "0 !important" },
                  }}
                >
                  <CustomCategorySelectSingle
                    label=""
                    name="status"
                    clearable={false}
                    searchable={false}
                    options={[...statusOptions]}
                    selectedValue={(watchAny("status") as string) || ""}
                    onValueChange={async (val) => {
                      // 必須項目。空選択 (プレースホルダー) は無視。
                      if (!val || !val.trim()) return;
                      // ステータスだけは収入/支出詳細と同様、「更新」ボタンを待たず即 PATCH。
                      const previous = (watchAny("status") as string) || "";
                      // 即時保存するのでフォームを dirty にしない。
                      setValue("status" as any, val as any, {
                        shouldDirty: false,
                        shouldTouch: true,
                      });
                      // 既存レコードのみ即時反映。id 不明 (新規等) は通常の更新に任せる。
                      if (!resolvedRepairId) return;
                      try {
                        await dispatch(
                          updateRepair({
                            repair_id: resolvedRepairId,
                            status: val,
                          }) as any,
                        ).unwrap();
                        // 表示はローカル currentData なので status を同期。
                        setCurrentData((prev: any) =>
                          prev ? { ...prev, status: val } : prev,
                        );
                        showToast({
                          message: "ステータスを更新しました",
                          type: "success",
                        });
                      } catch {
                        // 失敗時は元のステータスへ戻す。
                        setValue("status" as any, previous as any, {
                          shouldDirty: false,
                        });
                        showToast({
                          message: "ステータスの更新に失敗しました",
                          type: "error",
                        });
                      }
                    }}
                    inputWidth="100%"
                    fontSize={14}
                    menuWidth={200}
                    placeholder="ステータス"
                  />
                </Box>
              }
            />
          </Box>

          {/* 詳細情報 */}
          <Box component="form" onSubmit={handleSubmit(onSubmit, scrollToFirstError)}>
            <CustomSectionTitle title="基本情報" size="small" />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                mt: 2,
                maxWidth: "100%",
              }}
            >
              <CustomTextInput
                label="タイトル"
                name="title"
                placeholder=""
                register={registerAny}
                errors={errorsAny}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                isRequired
              />
              {/* ステータスは収入/支出詳細と同様、sticky タイトル行の右上へ移設。 */}
              <CustomSingleSelect
                label="場所区分"
                name="areaType"
                control={control}
                errors={errorsAny}
                options={AREA_TYPE_OPTIONS as any}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidthSp={"100%"}
                inputWidth={leftInputContainerWidth}
                isRequired
              />
              <Box sx={{ ml: 3, position: "relative" }}>
                <SubdirectoryArrowRightIcon
                  sx={{
                    position: "absolute",
                    left: -20,
                    top: 0,
                    fontSize: 16,
                    color: "#989898",
                    pointerEvents: "none",
                  }}
                />
                <CustomSingleSelect
                  label="名称"
                  name="detail"
                  control={control}
                  errors={errorsAny}
                  options={(() => {
                    const baseOptions =
                      watchedAreaType === "facility"
                        ? equipmentList.map((u) => ({
                            label: u.landmark_name,
                            value: u.landmark_name,
                          }))
                        : areaOptions.map((o) => ({
                            label: o.label,
                            value: o.value,
                          }));
                    // legacy データで areaType に対応しない名前が detail に残っている
                    // 場合、MUI Select が "out-of-range value" 警告を毎レンダリング
                    // 出し続けるため、現在値を fallback option として末尾に補う。
                    const currentDetail = watchAny("detail") as string;
                    if (
                      currentDetail &&
                      !baseOptions.some((o) => o.value === currentDetail)
                    ) {
                      baseOptions.push({
                        label: currentDetail,
                        value: currentDetail,
                      });
                    }
                    return baseOptions;
                  })()}
                  placeholder={
                    isLoadingAreas
                      ? "読み込み中..."
                      : (watchedAreaType === "facility"
                            ? equipmentList.length
                            : areaOptions.length) === 0
                        ? "該当する区画がありません"
                        : "選択してください"
                  }
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={`calc(${leftInputContainerWidth} - 24px)`}
                  inputWidthSp={"100%"}
                  disabled={
                    isLoadingAreas ||
                    (watchedAreaType === "facility"
                      ? equipmentList.length === 0
                      : areaOptions.length === 0)
                  }
                  onChangeValue={(value: any) => {
                    const v = String(value || "");
                    if (watchedAreaType === "facility") {
                      const u = equipmentList.find(
                        (x) => x.landmark_name === v,
                      );
                      setTargetUnitId(u?.unit_id);
                    } else {
                      const o = areaOptions.find((x) => x.value === v);
                      setTargetUnitId(o?.unit_id);
                    }
                  }}
                />
              </Box>
              <CustomSingleSelect
                label="支出区分"
                name="expenditure_type"
                control={control}
                errors={errorsAny}
                options={[
                  { label: "CAPEX（資本的支出）", value: "capex" },
                  { label: "OPEX（工事費）", value: "opex" },
                ]}
                placeholder="選択してください"
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
              />
              <CustomCategorySelect
                label="カテゴリ"
                name="category"
                options={categoryOptions}
                selectedCategories={selectedCategories}
                onCategoriesChange={(cats: string[]) => {
                  setSelectedCategories(cats);
                  setValue("category", cats.join(", ") as any, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                }}
                onAddNewCategory={handleAddNewCategory}
                onDeleteCategoryOption={handleDeleteCategoryOption}
                errors={errorsAny}
                labelWidth={labelWidth}
                inputWidth={leftInputContainerWidth}
              />
              {/* RHF にカテゴリ値を登録してダーティ判定を有効化 */}
              <input type="hidden" {...(register as any)("category")} />

              {/* 固定アクションバー（Figma準拠、PropertyOverviewと同様） */}
              <FixedActionBar
                submitLabel="更新"
                isLoading={isSubmitting}
                disabled={!isFormChanged || isSubmitting || analyzingEstimates}
                embedded={embedded}
              />
            </Box>
          </Box>

          {/* 画像 */}
          <Box >
            <CustomFileUploader
              label="画像"
              name="images"
              setValue={setValue as any}
              register={registerAny}
              initialImages={
                repairImages.map((url: string, index: number) => {
                  // URLからファイル名を抽出
                  const fileName =
                    url.split("/").pop() || `repair_image_${index}`;
                  // URLパラメータを除去してファイル名を取得
                  const cleanFileName = fileName.split("?")[0];
                  // デコードして日本語ファイル名に対応
                  const decodedFileName = decodeURIComponent(cleanFileName);
                  // UUIDの部分を除去（UUID_の形式で始まる場合）
                  const finalFileName = decodedFileName.replace(
                    /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}_/,
                    "",
                  );

                  return {
                    id: `repair_image_${index}`,
                    data: url,
                    originalFileName: finalFileName,
                    isUrl: true,
                  };
                }) as any
              }
              type="multiple"
              onImagesDeleted={handleRepairImagesDeleted}
              onImageChange={() => setImagesChanged(true)}
            />
          </Box>

          {/* AI見積査定結果（工事見積査定AI(AI-BPO)オプション契約時のみ表示＝機能ゲート/軸②） */}
          <Can feature={OPTION_CODES.ESTIMATE_AI}>
          <Box mt={4}>
            <CustomSectionTitle title="AI見積査定結果" size="small" />
            <Box
              data-collection="repair_estimates"
              data-collection-label="AI見積査定結果"
              sx={{
                display: "flex",
                gap: 2,
                mt: 2,
                flexWrap: "wrap",
              }}
            >
              {repairEstimates.length > 0 ? (
                repairEstimates.map((estimate, index: number) => {
                  const repairImageUrl = estimate?.repair_image || "";
                  const hasRepairImage = !!repairImageUrl;
                  const isPdfRepairImage =
                    hasRepairImage && /\.pdf(?:$|\?)/i.test(repairImageUrl);
                  const isProcessing = estimate.ai_assessed === false;
                  const isError = !!estimate.ai_error;

                  const isChecked =
                    Boolean(estimate.is_selected) ||
                    String(estimate.status || "") === "完了" ||
                    estimate.status === true;
                  const linkedExpenseId: string | undefined = (estimate as any)
                    ?.linked_expense_id;
                  return (
                    <Box
                      key={estimate.repair_estimate_id || index}
                      data-collection-item
                      sx={{
                        width: 328,
                        border: isError
                          ? "1px solid #FF4D4F"
                          : "1px solid #d9d9d9",
                        borderRadius: "10px",
                        backgroundColor: "#fff",
                        p: 1.5,
                        display: "flex",
                        gap: 1,
                        position: "relative",
                        opacity: isProcessing || isError ? 0.7 : 1,
                      }}
                    >
                      {/* 画面コンテキスト用 (pageContext.ts 参照):
                          各カードの主要値を data-field 付きで露出する。表示は
                          下のレイアウト Box が担うので、ここでは hidden span のみ。 */}
                      <span
                        data-field="repair_estimate_id"
                        data-label="見積ID"
                        style={{ display: "none" }}
                      >
                        {estimate.repair_estimate_id || ""}
                      </span>
                      <span
                        data-field="estimate_company_name"
                        data-label="会社名"
                        style={{ display: "none" }}
                      >
                        {estimate.estimate_company_name || ""}
                      </span>
                      <span
                        data-field="estimate_amount"
                        data-label="見積額"
                        style={{ display: "none" }}
                      >
                        {String(estimate.estimate_amount ?? "")}
                      </span>
                      <span
                        data-field="ai_overall_decision"
                        data-label="AI査定"
                        style={{ display: "none" }}
                      >
                        {String(estimate.ai_overall_decision ?? "")}
                      </span>
                      <span
                        data-field="status"
                        data-label="ステータス"
                        style={{ display: "none" }}
                      >
                        {String(estimate.status ?? "")}
                      </span>
                      <span
                        data-field="is_selected"
                        data-label="選定可否"
                        data-value-raw={String(isChecked)}
                        style={{ display: "none" }}
                      >
                        {isChecked ? "選定済み" : "未選定"}
                      </span>

                      {/* 通常の解析中オーバーレイ */}
                      {isProcessing && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "#6161618C",
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                          }}
                        >
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              sx={{
                                fontSize: 18,
                                color: "#FFFFFF",
                                fontWeight: 700,
                              }}
                            >
                              AI査定中...
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {isError && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "rgba(255, 255, 255, 0.10)",
                            backdropFilter: "blur(4px)",
                            borderRadius: "10px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                            gap: 0.5,
                          }}
                        >
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDeleteEstimate(
                                String(estimate.repair_estimate_id),
                              );
                            }}
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              p: 0.25,
                              color: "#3e3e3e",
                              "&:hover": {
                                backgroundColor: "rgba(62, 62, 62, 0.1)",
                              },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <Typography
                            sx={{
                              fontSize: 18,
                              color: "#FF4D4F",
                              fontWeight: 700,
                            }}
                          >
                            解析失敗
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: "#666666",
                              textAlign: "center",
                              px: 2,
                            }}
                          >
                            見積査定中にエラーが発生しました。
                          </Typography>
                          <CustomButton
                            label="再見積もり"
                            buttonCategory="submit"
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleReEstimate(
                                String(estimate.repair_estimate_id),
                              );
                            }}
                            sx={{
                              mt: 0.5,
                              fontSize: "12px !important",
                              height: "28px !important",
                              minWidth: "auto !important",
                            }}
                          />
                        </Box>
                      )}

                      <Box
                        sx={{
                          width: 121,
                          height: 121,
                          borderRadius: 1,
                          backgroundColor: isError
                            ? hasRepairImage
                              ? "#ffffff"
                              : "#e3e3e3"
                            : isProcessing
                              ? "#e3e3e3"
                              : hasRepairImage
                                ? "#ffffff"
                                : "#e3e3e3",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: isProcessing
                            ? "#989898"
                            : hasRepairImage
                              ? "#3e3e3e"
                              : "#989898",
                          fontWeight: 700,
                          overflow: "hidden",
                          position: "relative",
                          textAlign: "center",
                        }}
                      >
                        {isError ? (
                          hasRepairImage ? (
                            isPdfRepairImage ? (
                              <CustomPdfPreviewCanvas
                                pdfUrl={repairImageUrl}
                                width={121}
                                height={121}
                              />
                            ) : (
                              <Box
                                component="a"
                                href={repairImageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  display: "block",
                                }}
                              >
                                <Box
                                  component="img"
                                  src={repairImageUrl}
                                  alt="見積画像"
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </Box>
                            )
                          ) : null
                        ) : isProcessing ? (
                          <>
                            見積書
                            <br />
                            解析中
                          </>
                        ) : hasRepairImage ? (
                          isPdfRepairImage ? (
                            <CustomPdfPreviewCanvas
                              pdfUrl={repairImageUrl}
                              width={121}
                              height={121}
                            />
                          ) : (
                            <Box
                              component="a"
                              href={repairImageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "block",
                              }}
                            >
                              <Box
                                component="img"
                                src={repairImageUrl}
                                alt="見積画像"
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </Box>
                          )
                        ) : null}
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            onClick={() => {
                              if (estimate.ai_assessed && !estimate.ai_error) {
                                navigate(
                                  `/estimates/${estimate.repair_estimate_id}`,
                                  {
                                    state: {
                                      companyName:
                                        estimate.estimate_company_name ||
                                        "会社名不明",
                                      title: state?.repair?.title,
                                      amount: estimate.estimate_amount || 0,
                                      status: estimate.status || "待機中",
                                      repair_image: estimate.repair_image,
                                      detailed_evaluation:
                                        estimate.detailed_evaluation,
                                      anomaly_checks: estimate.anomaly_checks,
                                      ai_assessed: estimate.ai_assessed,
                                      repairId:
                                        currentData?.repair_id ||
                                        state?.repair?.id,
                                      propName: propertyName,
                                      repairTitle:
                                        currentData?.repair_title ||
                                        currentData?.title ||
                                        state?.repair?.title,
                                      propertyId: currentData?.property_id,
                                      estimateTitle:
                                        getEstimateDisplayTitle(estimate),
                                      fromSource: (state as any)?.fromSource,
                                      privateAreaId: (state as any)
                                        ?.privateAreaId,
                                      privateAreaName: (state as any)
                                        ?.privateAreaName,
                                      commonAreaId: (state as any)
                                        ?.commonAreaId,
                                      commonAreaName: (state as any)
                                        ?.commonAreaName,
                                    },
                                  },
                                );
                              }
                            }}
                            sx={{
                              fontWeight: 700,
                              fontSize: 14,
                              color: "#3e3e3e",
                              cursor:
                                estimate.ai_assessed && !estimate.ai_error
                                  ? "pointer"
                                  : "default",
                              "&:hover":
                                estimate.ai_assessed && !estimate.ai_error
                                  ? {
                                      textDecoration: "underline",
                                      color: "#0b9dbd",
                                    }
                                  : {},
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "150px",
                            }}
                          >
                            {estimate.estimate_company_name || "会社名不明"}
                          </Typography>
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDeleteEstimate(
                                String(estimate.repair_estimate_id),
                              );
                            }}
                            disabled={isProcessing}
                            sx={{
                              ml: 1,
                              width: 24,
                              height: 24,
                              color: "#3e3e3e",
                              "&:hover": {
                                backgroundColor: "rgba(62, 62, 62, 0.1)",
                              },
                              "&:disabled": {
                                color: "#9ca3af",
                              },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            px: 0.75,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 10,
                              color: "#3e3e3e",
                            }}
                          >
                            見積額
                          </Typography>
                          {isError ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 16,
                                  color: "#3e3e3e",
                                }}
                              >
                                -
                              </Typography>
                              <Typography
                                sx={{ fontSize: 10, color: "#3e3e3e" }}
                              >
                                円
                              </Typography>
                            </Box>
                          ) : isProcessing ? (
                            <Typography sx={{ fontSize: 10, color: "#989898" }}>
                              解析中
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 16,
                                  color: "#3e3e3e",
                                }}
                              >
                                {(() => {
                                  const raw = estimate.estimate_amount;
                                  const n =
                                    typeof raw === "number"
                                      ? raw
                                      : typeof raw === "string"
                                        ? Number(raw.replace(/[^0-9.-]/g, ""))
                                        : NaN;
                                  return Number.isFinite(n)
                                    ? n.toLocaleString("ja-JP")
                                    : "-";
                                })()}
                              </Typography>
                              <Typography
                                sx={{ fontSize: 10, color: "#3e3e3e" }}
                              >
                                円
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            px: 0.75,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 10,
                              color: "#3e3e3e",
                            }}
                          >
                            AI査定
                          </Typography>
                          {isError ? (
                            <Typography
                              sx={{
                                fontSize: 10,
                                color: "#3e3e3e",
                                fontWeight: 700,
                                backgroundColor: "#e3e3e3",
                                padding: "2px 4px",
                                borderRadius: "8px",
                              }}
                            >
                              不明
                            </Typography>
                          ) : isProcessing ? (
                            <Typography sx={{ fontSize: 10, color: "#989898" }}>
                              実行中
                            </Typography>
                          ) : (
                            <Chip
                              label={estimate.ai_overall_decision || "-"}
                              size="small"
                              sx={{
                                height: "20px",
                                fontSize: "10px",
                                fontWeight: 700,
                                backgroundColor: getOverallBackground(
                                  estimate.ai_overall_decision || "-",
                                ),
                                color: getOverallColor(
                                  estimate.ai_overall_decision || "-",
                                ),
                                "&:hover": {
                                  backgroundColor: getOverallBackground(
                                    estimate.ai_overall_decision || "-",
                                  ),
                                },
                              }}
                            />
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            px: 0.75,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 10,
                              color: "#3e3e3e",
                            }}
                          >
                            選定可否
                          </Typography>
                          {isError ? (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <CustomCheckbox
                                checked={false}
                                onChange={() => {}}
                              />
                            </Box>
                          ) : isProcessing ? (
                            <Box
                              sx={{
                                width: 88,
                                height: "23.8px",
                                border: "1px solid #d9d9d9",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 1.25,
                                backgroundColor: "#fff",
                              }}
                            >
                              <Typography
                                sx={{ fontSize: 12, color: "#989898" }}
                              >
                                AI査定中
                              </Typography>
                            </Box>
                          ) : isChecked ? (
                            linkedExpenseId ? (
                              <Box
                                component="span"
                                onClick={() => {
                                  const pid = currentData?.property_id || "";
                                  const params = new URLSearchParams();
                                  if (pid) params.set("property_id", pid);
                                  const qs = params.toString();
                                  navigate(
                                    `/expenses/${linkedExpenseId}${qs ? `?${qs}` : ""}`,
                                    {
                                      state: {
                                        propName: propertyName,
                                        propertyId: pid,
                                      },
                                    },
                                  );
                                }}
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#3E3E3E",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                  px: 0.5,
                                  "&:hover": { color: "#0E9DBC" },
                                }}
                              >
                                支出登録済み
                              </Box>
                            ) : (
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#3E3E3E",
                                  px: 0.5,
                                }}
                              >
                                選定済み
                              </Box>
                            )
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <CustomCheckbox
                                checked={false}
                                onChange={() =>
                                  setSelectModalEstimateId(
                                    String(estimate.repair_estimate_id),
                                  )
                                }
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              ) : (
                // <Typography sx={{ fontSize: 14, color: "#989898" }}>
                //   見積はまだ登録されていません
                // </Typography>
                <></>
              )}

              {pendingEstimateCount > 0 &&
                Array.from({ length: pendingEstimateCount }).map((_, idx) => (
                  <Box
                    key={`pending-estimate-${idx}`}
                    sx={{
                      width: 328,
                      border: "1px solid #d9d9d9",
                      borderRadius: "10px",
                      backgroundColor: "#fff",
                      p: 1.5,
                      display: "flex",
                      gap: 1,
                      position: "relative",
                      opacity: 0.7,
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "#6161618C",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                      }}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          sx={{
                            fontSize: 18,
                            color: "#FFFFFF",
                            fontWeight: 700,
                          }}
                        >
                          AI査定中...
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: 121,
                        height: 121,
                        borderRadius: 1,
                        backgroundColor: "#e3e3e3",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#989898",
                        fontWeight: 700,
                        textAlign: "center",
                      }}
                    >
                      見積書
                      <br />
                      解析中
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 14,
                            color: "#989898",
                          }}
                        >
                          解析中の見積書
                        </Typography>
                        <IconButton
                          disabled
                          sx={{
                            ml: 1,
                            width: 24,
                            height: 24,
                            color: "#9ca3af",
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          px: 0.75,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 10,
                            color: "#3e3e3e",
                          }}
                        >
                          見積額
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: "#989898" }}>
                          解析中
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          px: 0.75,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 10,
                            color: "#3e3e3e",
                          }}
                        >
                          ステータス
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: "#989898" }}>
                          AI査定中
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          px: 0.75,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 10,
                            color: "#3e3e3e",
                          }}
                        >
                          AI査定
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: "#989898" }}>
                          実行中
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}

              {!analyzingEstimates && !reEstimateInProgress && (
                <Box sx={{ mt: 0.3 }} key={estimateUploaderKey}>
                  <CustomFileUploader
                    key={estimateUploaderKey}
                    name={"estimate_images" as any}
                    setValue={setValue as any}
                    register={registerAny}
                    initialImages={(watchAny("estimate_images") as any[]) || []}
                    type="multiple"
                    onImageChange={handleEstimateImagesChange}
                    onImagesDeleted={handleEstimateImagesDeleted}
                    width="354px"
                  />
                </Box>
              )}
            </Box>
          </Box>
          </Can>

          {/* Phase 2: 関連経費セクション（非表示）
          {currentData?.property_id && currentData?.repair_id && (
            <LinkedExpensesSection
              propertyId={currentData.property_id}
              repairId={currentData.repair_id}
            />
          )}
          */}

          {/* 見積選定モーダル: 紐付き支出の有無で文言と挙動が変わる */}
          {(() => {
            if (!selectModalEstimateId) return null;
            const target = (currentData?.repair_estimates || []).find(
              (e: any) =>
                String(e.repair_estimate_id) === String(selectModalEstimateId),
            ) as any;
            const hasLinkedExpense = Boolean(target?.linked_expense_id);
            const closeModal = () => {
              if (!submittingSelect) setSelectModalEstimateId(null);
            };
            const runSelect = async (createExpense: boolean) => {
              if (!resolvedRepairId || !selectModalEstimateId) return;
              setSubmittingSelect(true);
              try {
                await dispatch(
                  selectRepairEstimate({
                    repair_id: resolvedRepairId,
                    repair_estimate_id: selectModalEstimateId,
                    create_expense: createExpense,
                  }),
                ).unwrap();
                showToast({
                  message: hasLinkedExpense
                    ? "支払い済みに更新しました"
                    : createExpense
                      ? "支出を登録しました"
                      : "選定済みにしました",
                  type: "success",
                });
                // 最新の修繕データを取り直して local state に反映
                const refreshed: any = await dispatch(
                  fetchRepairById({ repair_id: resolvedRepairId }),
                ).unwrap();
                if (refreshed) setCurrentData(refreshed);
                setSelectModalEstimateId(null);
              } catch (err: any) {
                showToast({
                  message: typeof err === "string" ? err : "選定に失敗しました",
                  type: "error",
                });
              } finally {
                setSubmittingSelect(false);
              }
            };
            return (
              <CustomModal
                openModal={Boolean(selectModalEstimateId)}
                handleCloseModal={closeModal}
                title={hasLinkedExpense ? "支払い済みに更新" : "支出として登録"}
                bodyText=""
                modalType="deleteModal"
                hideCloseButton
              >
                <Box>
                  <Typography sx={{ fontSize: "14px", color: "#666" }}>
                    {hasLinkedExpense
                      ? "この見積に紐づく支出のステータスを「支払い済み」に更新しますか?"
                      : "この見積の内容を支出として登録しますか?"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "center",
                  }}
                >
                  {hasLinkedExpense ? (
                    <>
                      <CustomButton
                        label="キャンセル"
                        buttonCategory="cancelModal"
                        disabled={submittingSelect}
                        onClick={closeModal}
                      />
                      <CustomButton
                        label="はい"
                        buttonCategory="submit"
                        disabled={submittingSelect}
                        onClick={() => runSelect(false)}
                        sx={{ width: "50% !important" }}
                      />
                    </>
                  ) : (
                    <>
                      <CustomButton
                        label="いいえ"
                        buttonCategory="cancelModal"
                        disabled={submittingSelect}
                        onClick={() => runSelect(false)}
                      />
                      <CustomButton
                        label="はい"
                        buttonCategory="submit"
                        disabled={submittingSelect}
                        onClick={() => runSelect(true)}
                        sx={{ width: "50% !important" }}
                      />
                    </>
                  )}
                </Box>
              </CustomModal>
            );
          })()}
        </Box>
      )}
    </CustomFormNavigationGuard>
  );
};

export default RepairDetailPage;
